import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowLeft } from "lucide-react";

const STEPS = [
  {
    key: "notes",
    number: "01",
    title: "The notes",
    lede: "Which olfactory territories move you? Choose as many as you like.",
    optionKey: "notes",
    multi: true,
    min: 1,
  },
  {
    key: "budget",
    number: "02",
    title: "The budget",
    lede: "Every price bracket has its own poetry. What is yours?",
    optionKey: "budgets",
    multi: false,
    min: 1,
  },
  {
    key: "occasion",
    number: "03",
    title: "The occasion",
    lede: "Where will this fragrance live on your skin?",
    optionKey: "occasions",
    multi: false,
    min: 1,
  },
  {
    key: "climate",
    number: "04",
    title: "The climate",
    lede: "Weather shapes a scent's projection. Set the stage.",
    optionKey: "climates",
    multi: false,
    min: 1,
  },
];

export default function StepForm({ options, onSubmit }) {
  const [idx, setIdx] = useState(0);
  const [state, setState] = useState({
    notes: [],
    budget: "",
    occasion: "",
    climate: "",
    gender: "any",
  });

  const step = STEPS[idx];
  const values =
    step.optionKey === "budgets"
      ? options.budgets
      : options[step.optionKey];

  const isChosen = (v) => {
    if (step.multi) return state[step.key].includes(v);
    return state[step.key] === v;
  };

  const toggle = (v) => {
    if (step.multi) {
      setState((s) => ({
        ...s,
        [step.key]: s[step.key].includes(v)
          ? s[step.key].filter((x) => x !== v)
          : [...s[step.key], v],
      }));
    } else {
      setState((s) => ({ ...s, [step.key]: v }));
    }
  };

  const canProceed = step.multi
    ? state[step.key].length >= step.min
    : Boolean(state[step.key]);

  const next = () => {
    if (idx < STEPS.length - 1) setIdx(idx + 1);
    else onSubmit(state);
  };
  const back = () => idx > 0 && setIdx(idx - 1);

  return (
    <section
      className="min-h-screen w-full px-6 sm:px-12 lg:px-20 py-10 lg:py-14 flex flex-col"
      data-testid="form-section"
    >
      {/* Header row */}
      <div className="flex items-center justify-between border-b border-border pb-6">
        <span className="label-caps text-foreground/60">Scent Match</span>
        <div className="flex items-center gap-2" data-testid="progress-indicator">
          {STEPS.map((s, i) => (
            <span
              key={s.key}
              className="h-px transition-all"
              style={{
                width: i === idx ? "44px" : "22px",
                backgroundColor:
                  i <= idx ? "hsl(var(--gold))" : "hsl(var(--border))",
              }}
            />
          ))}
        </div>
      </div>

      {/* Step body */}
      <div className="flex-1 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 pt-14 lg:pt-24">
        {/* Left — big number + title */}
        <div className="lg:col-span-4">
          <AnimatePresence mode="wait">
            <motion.div
              key={step.key}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
            >
              <div className="step-num" data-testid="step-number">
                {step.number}
              </div>
              <h2 className="font-serif text-4xl sm:text-5xl mt-6 text-foreground">
                {step.title}
              </h2>
              <p className="text-foreground/60 mt-6 max-w-sm leading-relaxed">
                {step.lede}
              </p>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Right — chip grid */}
        <div className="lg:col-span-8">
          <AnimatePresence mode="wait">
            <motion.div
              key={step.key}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
              className="flex flex-wrap gap-3"
              data-testid={`chip-group-${step.key}`}
            >
              {values.map((v) => {
                const value = typeof v === "string" ? v : v.id;
                const label = typeof v === "string" ? v : v.label;
                return (
                  <button
                    key={value}
                    type="button"
                    onClick={() => toggle(value)}
                    className={`chip ${isChosen(value) ? "selected" : ""}`}
                    data-testid={`chip-${step.key}-${value}`}
                  >
                    {label}
                  </button>
                );
              })}
            </motion.div>
          </AnimatePresence>

          {/* Gender extra on last step */}
          {step.key === "climate" && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="mt-14 border-t border-border pt-10"
            >
              <div className="label-caps text-foreground/60 mb-4">
                Gender leaning · optional
              </div>
              <div className="flex flex-wrap gap-3">
                {options.genders.map((g) => (
                  <button
                    key={g}
                    type="button"
                    onClick={() =>
                      setState((s) => ({ ...s, gender: g }))
                    }
                    className={`chip ${state.gender === g ? "selected" : ""}`}
                    data-testid={`chip-gender-${g}`}
                  >
                    {g}
                  </button>
                ))}
              </div>
            </motion.div>
          )}
        </div>
      </div>

      {/* Footer nav */}
      <div className="border-t border-border pt-6 mt-10 flex items-center justify-between">
        <button
          onClick={back}
          disabled={idx === 0}
          className="btn-sharp"
          style={{ opacity: idx === 0 ? 0.35 : 1 }}
          data-testid="form-back-btn"
        >
          <ArrowLeft size={14} strokeWidth={1.5} />
          Back
        </button>
        <span className="label-caps text-foreground/50 hidden sm:inline">
          {idx + 1} / {STEPS.length}
        </span>
        <button
          onClick={next}
          disabled={!canProceed}
          className="btn-sharp gold"
          data-testid="form-next-btn"
        >
          {idx === STEPS.length - 1 ? "Reveal my matches" : "Continue"}
          <ArrowRight size={14} strokeWidth={1.5} />
        </button>
      </div>
    </section>
  );
}
